import { DateValue } from '@internationalized/date';
import { DatePickerProps as DatePickerBase } from '@react-types/datepicker';
import { MutableRefObject, useRef } from 'react';
import { mergeProps, useButton, useDatePicker, useFocusWithin, VisuallyHidden } from 'react-aria';
import { useDatePickerState } from 'react-stately';

import Calendar from 'design-system/components/DatePicker/Calendar/Calendar';
import CalendarPopover from 'design-system/components/DatePicker/CalendarPopover';
import DateField from 'design-system/components/DatePicker/DateField';

export interface DatePickerProps extends DatePickerBase<DateValue> {}

const OpenButton = ({ calendarRef }: { calendarRef: MutableRefObject<HTMLElement> }) => {
  const openCalBtnRef = useRef();
  const { buttonProps } = useButton(
    {
      onPress: () => {
        if (calendarRef.current) {
          (
            calendarRef.current.querySelector('button[aria-label="Previous"]') as HTMLButtonElement
          ).focus();
        }
      },
    },
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    openCalBtnRef,
  );

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return <button {...buttonProps} ref={openCalBtnRef} aria-label='Open calendar' />;
};

const DatePicker = (props: DatePickerProps) => {
  const state = useDatePickerState(props);
  const ref = useRef(null);
  const { groupProps, fieldProps, calendarProps } = useDatePicker(props, state, ref);
  const calendarRef = useRef(null);
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => {
      if (!state.isOpen) {
        state.setOpen(true);
      }
    },
    onBlurWithin: (e) => {
      // Close calendar if next target is not within the calendar
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (!calendarRef?.current?.contains(e.relatedTarget) && state.isOpen) {
        state.setOpen(false);
      }
    },
  });

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
      <div {...mergeProps(groupProps, focusWithinProps)} ref={ref} style={{ display: 'flex' }}>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        <DateField {...mergeProps(fieldProps)} />
        <VisuallyHidden>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          <OpenButton calendarRef={calendarRef} />
        </VisuallyHidden>
      </div>
      {state.isOpen && (
        <CalendarPopover state={state} triggerRef={ref} placement='bottom start'>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          <Calendar {...{ ...calendarProps, autoFocus: null }} calendarRef={calendarRef} />
        </CalendarPopover>
      )}
    </div>
  );
};

export default DatePicker;
